import React, { useCallback, useContext, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import ManualKpiRowLeft from './ManualKpiRowLeft';
import ScorecardContext from '../../../../contexts/ScorecardContext';
import AnalyticsContext from '../../../../contexts/AnalyticsContext';
import LocalTimelineContext from '../../../../contexts/Timeline/LocalTimelineContext';
import { ScoringBandColors } from '../../../../screens/ScorecardsIndex/constants';
import usePopup from '../../../../hooks/usePopup';
import ConfirmationModal from '../../../ConfirmationModal';
import ManualRowForm from '../ManualRowForm';
import ScorecardUsageWarning from '../../ScorecardUsageWarning';
import Typography from 'kingpin/atoms/Typography';

const DeleteKpiConfirmationBody = ({
  kpi,
}: {
  kpi: Scorecards.ManualKpiRow;
}) => {
  const { scorecard } = useContext(ScorecardContext);

  return (
    <div>
      <ScorecardUsageWarning scorecard={scorecard} />
      <Typography.Body type="Body 12">
        {`Are you sure you want to delete the ${kpi.label} KPI?`}
      </Typography.Body>
    </div>
  );
};

const ManualKpiRowLeftContainer = ({
  kpi,
  dragHandleProps,
  isLastRow,
}: {
  kpi: Scorecards.ManualKpiRow;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isLastRow?: boolean;
}) => {
  const {
    scorecard,
    updateScorecard,
    setManualKpiIdBeingEdited,
    manualKpiIdBeingEdited,
    kpiBeingHighlighted,
    hoveredKpiId,
    setHoveredKpiId,
  } = useContext(ScorecardContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { addEvent } = useContext(LocalTimelineContext);
  const [numColors] = useState<number>(
    () => ScoringBandColors[scorecard.scoringBandId].length,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { isOpen: isEditOpen, open: openEdit, close: closeEdit } = usePopup();
  const {
    isOpen: isCloseConfirmationOpen,
    open: openCloseConfirmation,
    close: closeCloseConfirmation,
  } = usePopup();
  const {
    isOpen: isConfirmDeleteOpen,
    open: openConfirmDelete,
    close: closeConfirmDelete,
  } = usePopup();

  const onCloseClicked = useCallback(() => {
    if (hasUnsavedChanges) {
      openCloseConfirmation();
      return;
    }

    closeEdit();
  }, [closeEdit, hasUnsavedChanges, openCloseConfirmation]);

  const onCloseConfirmed = useCallback(() => {
    closeCloseConfirmation();
    closeEdit();
    setHasUnsavedChanges(false);
  }, [closeEdit, closeCloseConfirmation]);

  const onDeleteConfirmed = useCallback(() => {
    const newKpis = scorecard.kpis.filter((k) => k.id !== kpi.id);
    const newScorecard = {
      ...scorecard,
      kpis: newKpis,
    };

    updateScorecard(newScorecard).then(async () => {
      if (addEvent) {
        await addEvent({
          actionText: 'removed Manual KPI',
          contextText: kpi.label,
        });
      }

      trackEvent('KPI List - Manual KPI - Deleted', {
        scorecardName: scorecard.title,
        scorecardId: scorecard.id,
      });
    });
  }, [addEvent, kpi.id, kpi.label, scorecard, trackEvent, updateScorecard]);

  const onDeleteClicked = useCallback(() => {
    openConfirmDelete();
  }, [openConfirmDelete]);

  const onEditClicked = useCallback(() => {
    trackEvent('KPI List - Manual KPI - Edit Clicked', {
      scorecardName: scorecard.title,
      scorecardId: scorecard.id,
    });
    openEdit();
  }, [openEdit, scorecard.id, scorecard.title, trackEvent]);

  const onEnterValuesClicked = useCallback(() => {
    trackEvent('KPI List - Manual KPI - Enter Values Clicked', {
      scorecardName: scorecard.title,
      scorecardId: scorecard.id,
    });
    setManualKpiIdBeingEdited(kpi.id);
  }, [
    kpi.id,
    scorecard.id,
    scorecard.title,
    setManualKpiIdBeingEdited,
    trackEvent,
  ]);

  const onRowHover = useCallback(() => {
    setHoveredKpiId(kpi.id);
  }, [kpi.id, setHoveredKpiId]);

  return (
    <>
      <ManualKpiRowLeft
        dragHandleProps={dragHandleProps}
        kpi={kpi}
        onEditClicked={onEditClicked}
        onEnterValuesClicked={onEnterValuesClicked}
        onDeleteClicked={onDeleteClicked}
        isLastRow={isLastRow}
        manualKpiIdBeingEdited={manualKpiIdBeingEdited}
        isHighlighted={kpiBeingHighlighted === kpi.id}
        isHovered={hoveredKpiId === kpi.id}
        onRowHover={onRowHover}
      />

      <ConfirmationModal
        isOpen={isConfirmDeleteOpen}
        close={closeConfirmDelete}
        title={'Delete KPI?'}
        bodyComponent={<DeleteKpiConfirmationBody kpi={kpi} />}
        confirmText={'Delete'}
        onConfirmed={onDeleteConfirmed}
      />

      <ModalTransition>
        {isEditOpen && (
          <Modal
            shouldScrollInViewport={false}
            autoFocus={false}
            width={Math.max(600, numColors * 150)}
          >
            <ManualRowForm
              close={onCloseClicked}
              manualKpi={kpi}
              setHasUnsavedChanges={setHasUnsavedChanges}
              isCloseConfirmationOpen={isCloseConfirmationOpen}
              onCloseConfirmed={onCloseConfirmed}
              onCloseConfirmationClosed={closeCloseConfirmation}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default ManualKpiRowLeftContainer;
